import { graphql } from "gatsby";
import React from "react";
import ErrorFallback from "../components/ErrorFallback";
import Page from "../components/Page";
import type { PayloadGeneralError } from "../types/payload";
import type { SiteData, HomePageData } from "../components/Site";
import type { VideoCard } from "../components/video/types";
import type { ImageCard } from "../components/image/types";
import type { MicroCopy as MicroCopyData } from "../components/MicroCopy";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { HouseType } from "../components/visualiser/Types";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";

export type GeneralErrorData = Pick<
  PayloadGeneralError,
  "title" | "subtitle"
> & {
  cta:
    | [
        Pick<PayloadGeneralError["cta"][0], "label"> & {
          linkedPage: {
            value: Pick<
              PayloadGeneralError["cta"][0]["linkedPage"]["value"],
              "path"
            >;
          };
        }
      ]
    | null;
  featuredVideo: VideoCard | null;
  featuredMedia: ImageCard | null;
};

export type Data = {
  payloadGeneralError: GeneralErrorData;
  payloadSite: SiteData;
  allPayloadMicroCopy: {
    nodes: MicroCopyData[];
  };
  payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
  allPayloadHouseModel: {
    nodes: HouseType[];
  };
  payloadHomePage: HomePageData;
  payloadCountryNavigation: CountryNavigation;
  payloadSampleOrder: SampleOrder | null;
  payloadVisualiser: Visualiser | null;
};

type Props = {
  data: Data;
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

const GeneralError = ({ data, pageContext }: Props) => {
  const siteData = data.payloadSite;
  const title = data.payloadGeneralError.title;

  return (
    <Page
      title={title}
      pageData={{ breadcrumbs: null, signupBlock: null, meta: null, path: "" }}
      siteData={siteData}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
    >
      <ErrorFallback
        countryCode={siteData.countryCode}
        generalError={data.payloadGeneralError}
      />
    </Page>
  );
};

export default GeneralError;

export const pageQuery = graphql`
  query GeneralErrorPage {
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }

  fragment GeneralErrorFragment on PayloadGeneralError {
    title
    subtitle
    cta {
      label
      linkedPage {
        value {
          ... on PayloadPage {
            slug
            path
          }
        }
      }
    }
    featuredMedia {
      ...ImageCardFragment
    }
    featuredVideo {
      ...VideoCardFragment
    }
  }
`;
